import React, { createContext } from 'react'
import { useMe, signOut } from '../api/auth'
import type { UserProfileResponse } from '../types/auth'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

interface AuthContextValue {
  user: UserProfileResponse | null
  isLoading: boolean
  isAuthenticated: boolean
  error: unknown
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export { AuthContext }

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient()
  const { data: user, isLoading, error } = useMe()

  const isAuthenticated = !!user && !error
  
  const logout = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Logout API call failed:', error)
      // Continue with local logout even if API call fails
    } finally {
      // Clear React Query cache to remove user data
      queryClient.clear()
      // Optionally refresh the page to ensure clean state
      window.location.reload()
    }
  }

  const value: AuthContextValue = {
    user: user || null,
    isLoading,
    isAuthenticated,
    error,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}